import CreateInventoryPosition from './CreateInventoryPosition';
import useAddress from '@/application/read/web3/useAddress';
import type { SelectInventoryFormFields } from '@/components/forms/CreatePositionForms/SelectInventoryForm';
import { useMemo, useReducer, useState } from 'react';
import useUserAssets from '@/application/read/assets/useUserAssets';
import useVault from '@/application/read/vaults/useVault';
import {
  INVENTORY_SHARE,
  TokenId,
  WeiPerEther,
  Zero,
  calculatePoolAprs,
} from 'nftx.js';
import useInventoryPool from '@/application/read/pools/useInventoryPool';
import usePositions from '@/application/read/positions/usePositions';
import Processing from './Processing';
import Success from './Success';
import { parseEther } from 'viem';
import { CreateInventoryPositionDetails, Step } from './types';
import Sidebar from './Sidebar';
import useBalances from '@/application/read/tokens/useBalances';
import getWeakestStatus from '@/presentation/utils/getWeakestStatus';
import CreateInventoryPositionLoading from './Loading';
import CreateInventoryPositionDisconnected from './Disconnected';

export type CreateInventoryPositionContainerProps = {
  vaultId: string;
  withinModal?: boolean;
  items?: SelectInventoryFormFields['items'];
  using?: 'nft' | 'vtoken';
};

const reducer = (
  state: CreateInventoryPositionDetails,
  action: Partial<CreateInventoryPositionDetails>
) => {
  return { ...state, ...action };
};

const CreateInventoryPositionContainer = ({
  vaultId,
  using = 'nft',
  withinModal,
  items = [],
}: CreateInventoryPositionContainerProps) => {
  const [step, setStep] = useState<Step>('using');
  const initialState: CreateInventoryPositionDetails = {
    items,
    using,
    vTokenAmount: '',
  };
  const [state, dispatch] = useReducer(reducer, initialState);

  const steps = useMemo((): Step[] => {
    if (state.using === 'nft') {
      return ['using', 'inventory', 'processing', 'success'];
    }
    return ['using', 'vtoken', 'processing', 'success'];
  }, [state.using]);

  const tokenIds = useMemo(() => {
    return (
      state.items.map(({ tokenId, quantity }): [TokenId, number] => [
        tokenId,
        Number(quantity || 0),
      ]) ?? []
    );
  }, [state.items]);

  const userAddress = useAddress();
  const { data: vault, status: vaultStatus } = useVault({ vaultId });
  const { data: allAssets, status: assetsStatus } = useUserAssets(
    {
      userAddress,
      vaultId,
    },
    { enabled: !!(vaultId && userAddress) }
  );
  const assets = useMemo(() => {
    return allAssets?.filter((asset) => asset.mintable);
  }, [allAssets]);

  const { data: pool } = useInventoryPool(vaultId);
  const { data: positionsData, data: [positions = []] = [] } = usePositions({
    userAddress,
    vaultId,
  });

  const hasExistingPosition = positions.length > 0;

  const currentShare = useMemo(() => {
    return positions.reduce(
      (total, position) => total + position.poolShare,
      Zero
    );
  }, [positions]);

  const currentApr = pool?.apr['1m'] ?? Zero;

  const vTokenToAdd = useMemo(() => {
    return state.using === 'nft'
      ? parseEther(`${state.items.length}`)
      : parseEther(`${state.vTokenAmount || 0}`);
  }, [state.items.length, state.using, state.vTokenAmount]);

  const newApr = useMemo(() => {
    if (!pool || !vault) {
      return 0n;
    }
    const newTokens = vTokenToAdd;
    if (!newTokens) {
      return currentApr;
    }
    const newValue = (newTokens * vault.vTokenToEth) / WeiPerEther;

    const aprs = calculatePoolAprs({
      createdAt: vault.createdAt,
      periodFees: pool.periodFees,
      share: INVENTORY_SHARE,
      poolValue: pool.vTokenValue + newValue,
    });

    return aprs['1m'];
  }, [currentApr, pool, vTokenToAdd, vault]);

  const newShare = useMemo(() => {
    if (!pool) {
      return 0n;
    }
    // The total amount of vToken staked by the user (across all of their xNFTs)
    const currentStakedVToken = positions.reduce(
      (total, position) => total + position.vToken,
      Zero
    );
    // The amount of vToken they're adding now
    if (!vTokenToAdd) {
      return currentShare;
    }
    // The total amount of vToken in the entire pool
    const currentPoolVToken = pool.vToken;

    const newStakedVToken = currentStakedVToken + vTokenToAdd;
    const newPoolVToken = currentPoolVToken + vTokenToAdd;

    if (!newPoolVToken) {
      return WeiPerEther;
    }

    // The % of the total pool that will belong to the user
    return (newStakedVToken * WeiPerEther) / newPoolVToken;
  }, [currentShare, pool, positions, vTokenToAdd]);
  const relativeLockTime = '48 hours';

  const { data: balances, status: balancesStatus } = useBalances({
    userAddress,
  });

  const maxAmount = useMemo(() => {
    if (state.using !== 'vtoken') {
      return 0n;
    }
    return balances?.find((b) => b.vaultId === vaultId)?.balance ?? Zero;
  }, [balances, state.using, vaultId]);

  const onNextStep = () => {
    const nextStep = steps[steps.findIndex((x) => x === step) + 1];
    setStep(nextStep);
  };
  const onPreviousStep = () => {
    const previousStep = steps[steps.findIndex((x) => x === step) - 1];
    setStep(previousStep);
  };

  if (!userAddress) {
    return <CreateInventoryPositionDisconnected />;
  }

  if (!vault) {
    return <CreateInventoryPositionLoading />;
  }

  const status = getWeakestStatus(balancesStatus, assetsStatus, vaultStatus);

  return (
    <CreateInventoryPosition
      assetName={vault.asset.name || ''}
      is1155={vault.is1155}
      assetAddress={vault.asset.id}
      assets={assets || []}
      withinModal={withinModal}
      dispatch={dispatch}
      state={state}
      step={step}
      status={status}
      symbol={vault.token.symbol}
      vaultId={vaultId}
      maxAmount={maxAmount}
      onNextStep={onNextStep}
      Sidebar={
        <Sidebar
          assetAddress={vault.asset.id}
          assetName={vault.asset.name}
          assets={assets || []}
          currentApr={currentApr}
          currentShare={currentShare}
          hasExistingPosition={hasExistingPosition}
          newApr={newApr}
          newShare={newShare}
          relativeLockTime={relativeLockTime}
          selectedItems={state.items}
          using={state.using}
          dispatch={dispatch}
          step={step}
          onPrevious={onPreviousStep}
          Processing={
            step === 'processing' && (
              <Processing
                onCancel={() =>
                  setStep(state.using === 'nft' ? 'inventory' : 'vtoken')
                }
                vault={vault}
                userAddress={userAddress}
                tokenIds={tokenIds}
                onSuccess={() => setStep('success')}
                using={state.using}
                vTokenAmount={state.vTokenAmount}
              />
            )
          }
          Success={step === 'success' && <Success vaultId={vaultId} />}
          vTokenAmount={state.vTokenAmount}
          withinModal={withinModal}
        />
      }
    />
  );
};

export default CreateInventoryPositionContainer;
